<template>
  <div class="lang-switcher dropdown">
    <button
      class="btn lang-toggle"
      :class="compact ? 'lang-toggle-compact' : ''"
      type="button"
      :id="dropdownId"
      data-bs-toggle="dropdown"
      data-bs-auto-close="true"
      aria-expanded="false"
      :aria-label="$t('lang.choose')"
      :title="$t('lang.current', { name: activeLocale.nativeName })"
    >
      <i class="bi bi-globe2 lang-globe" aria-hidden="true"></i>
      <span v-if="!compact" class="lang-code">{{ activeCodeLabel }}</span>
      <span
        v-if="loading"
        class="spinner-border spinner-border-sm ms-1"
        role="status"
        aria-hidden="true"
      ></span>
    </button>

    <div
      class="dropdown-menu dropdown-menu-end lang-menu"
      :aria-labelledby="dropdownId"
    >
      <div class="lang-menu-header">
        <span class="lang-menu-title">{{ $t("lang.choose") }}</span>
        <p class="lang-menu-note">{{ $t("lang.diasporaNote") }}</p>
      </div>

      <div class="lang-menu-scroll">
        <template v-for="group in groups" :key="group.id">
          <h6 class="lang-group-label">{{ $t(group.labelKey) }}</h6>

          <button
            v-for="locale in group.locales"
            :key="locale.code"
            type="button"
            class="lang-option"
            :class="{ 'is-active': locale.code === current }"
            :lang="locale.htmlLang"
            :aria-current="locale.code === current ? 'true' : 'false'"
            @click="select(locale.code)"
          >
            <span class="lang-option-body">
              <span class="lang-name">{{ locale.nativeName }}</span>
              <span class="lang-region">{{ $t(locale.regionKey) }}</span>
            </span>
            <i
              v-if="locale.code === current"
              class="bi bi-check2 lang-check"
              aria-hidden="true"
            ></i>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { groupedLocales, getLocale } from "@/i18n/locales";
import { setLocale, state } from "@/i18n";

export default {
  name: "LanguageSwitcher",
  props: {
    /** Icon-only variant used in the mobile header. */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      groups: groupedLocales(),
    };
  },
  computed: {
    dropdownId() {
      return this.compact ? "langDropdownMobile" : "langDropdownDesktop";
    },
    current() {
      return state.locale;
    },
    loading() {
      return state.loading;
    },
    activeLocale() {
      return getLocale(state.locale);
    },
    activeCodeLabel() {
      return state.locale.toUpperCase();
    },
  },
  methods: {
    select(code) {
      if (code === this.current) return;
      // Catalogues load lazily; the engine handles failures by staying put.
      setLocale(code);
    },
  },
};
</script>

<style scoped>
.lang-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e4e4ea;
  border-radius: 50px;
  padding: 6px 14px;
  font-size: 14px;
  color: #7f7f90;
  background: #fff;
  white-space: nowrap;
}

.lang-toggle:hover,
.lang-toggle:focus {
  color: #ce1212;
  border-color: rgba(206, 18, 18, 0.35);
}

.lang-globe {
  font-size: 1.05rem;
  line-height: 1;
}

.lang-code {
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Mobile: circular icon button matching the download button treatment. */
.lang-toggle-compact {
  width: 40px;
  height: 40px;
  padding: 0;
  justify-content: center;
  border-radius: 50%;
  border-color: #e4e4ea;
}

.lang-menu {
  width: 320px;
  max-width: calc(100vw - 24px);
  border: none;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.14);
  padding: 0;
  overflow: hidden;
}

.lang-menu-header {
  padding: 14px 16px 10px;
  border-bottom: 1px solid #f0f0f4;
}

.lang-menu-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #37373f;
}

.lang-menu-note {
  margin: 4px 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #9a9aa8;
}

.lang-menu-scroll {
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.lang-group-label {
  margin: 8px 8px 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #b6b6c2;
}

.lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 9px;
  background: transparent;
  text-align: start;
  cursor: pointer;
}

.lang-option:hover,
.lang-option:focus-visible {
  background: rgba(206, 18, 18, 0.07);
  outline: none;
}

.lang-option.is-active {
  background: rgba(206, 18, 18, 0.09);
}

.lang-option-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.lang-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: #37373f;
  line-height: 1.3;
}

.lang-option.is-active .lang-name {
  font-weight: 700;
  color: #bb2131;
}

.lang-region {
  font-size: 0.74rem;
  color: #9a9aa8;
  line-height: 1.35;
  margin-top: 2px;
  /* Region lists get long; keep rows to a predictable height. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lang-check {
  color: #ce1212;
  font-size: 1rem;
  flex-shrink: 0;
}
</style>
